import React, { useState } from "react";
import { Link2 } from "lucide-react";
import { Modal, FieldLabel, inputCls, InlineMsg } from "../../apikeys/shared";
import { Loader2 } from "lucide-react";

const MIN_REASON = 10;
const today = () => new Date().toISOString().slice(0, 10);

// Add or edit a supersession link (legacy → replacement) with a mandatory reason.
export default function LinkFormModal({ link, onSubmit, onClose }) {
  const editing = !!link;
  const [f, setF] = useState({
    legacy_sku: link?.legacy_sku || "", replacement_sku: link?.replacement_sku || "",
    effective_date: link?.effective_date || today(), type: link?.type || "direct", ratio: String(link?.ratio ?? 1), reason: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.value }));
  const ok = f.legacy_sku.trim() && f.replacement_sku.trim() && f.effective_date && Number(f.ratio) > 0 && f.reason.trim().length >= MIN_REASON;

  async function submit(e) {
    e.preventDefault();
    if (!ok || busy) return;
    setBusy(true); setError(null);
    try {
      await onSubmit({ ...f, legacy_sku: f.legacy_sku.trim(), replacement_sku: f.replacement_sku.trim(), ratio: Number(f.ratio), reason: f.reason.trim() });
      onClose();
    } catch (err) { setError(err.message); setBusy(false); }
  }

  return (
    <Modal title={editing ? `Edit link · ${link.legacy_sku}` : "Add supersession link"} icon={Link2} onClose={onClose} busy={busy}
           subtitle="Legacy history joins the replacement SKU · re-runs the pipeline" testid="link-form">
      <form onSubmit={submit} className="p-5 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldLabel label="Legacy SKU"><input data-testid="link-form-legacy" value={f.legacy_sku} onChange={set("legacy_sku")} disabled={editing || busy} className={inputCls} placeholder="1R-HVAC-CAP-90001" /></FieldLabel>
          <FieldLabel label="Replacement SKU"><input data-testid="link-form-replacement" value={f.replacement_sku} onChange={set("replacement_sku")} disabled={busy} className={inputCls} placeholder="1R-HVAC-CAP-10007" /></FieldLabel>
          <FieldLabel label="Effective date"><input data-testid="link-form-date" type="date" value={f.effective_date} onChange={set("effective_date")} disabled={busy} className={inputCls} /></FieldLabel>
          <div className="grid grid-cols-2 gap-3">
            <FieldLabel label="Type">
              <select data-testid="link-form-type" value={f.type} onChange={set("type")} disabled={busy} className={inputCls}>
                <option value="direct">Direct</option><option value="universal">Universal</option>
              </select>
            </FieldLabel>
            <FieldLabel label="Ratio" hint="units of replacement per legacy unit"><input data-testid="link-form-ratio" type="number" step="any" min="0.01" max="100" value={f.ratio} onChange={set("ratio")} disabled={busy} className={inputCls} /></FieldLabel>
          </div>
        </div>
        <FieldLabel label="Reason (required)" hint={`${f.reason.trim().length}/${MIN_REASON} min`}>
          <textarea data-testid="link-form-reason" rows={2} value={f.reason} onChange={set("reason")} disabled={busy} maxLength={500}
                    placeholder="e.g. Manufacturer bulletin SB-2026-014 supersedes the legacy capacitor." className={`${inputCls} font-sans resize-none`} />
        </FieldLabel>
        {error && <InlineMsg testid="link-form-error">{error}</InlineMsg>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} disabled={busy} data-testid="link-form-cancel" className="px-3 py-1.5 text-[11.5px] border border-border rounded-sm hover:bg-surface-2">Cancel</button>
          <button type="submit" disabled={!ok || busy} data-testid="link-form-submit"
                  className="px-4 py-1.5 text-[11.5px] font-semibold bg-primary text-primary-fg rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
            {busy && <Loader2 size={11} className="animate-spin" />}{busy ? "Re-running pipeline…" : editing ? "Save link" : "Add link"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
